import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  Collapse,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { navGroups } from "./navConfig";
import { tokens } from "../../theme";

export const SIDEBAR_WIDTH = 270;

function SidebarContent({ onNavigate }) {
  const location = useLocation();

  // A group starts open if it contains the active route.
  const groupHasActive = (g) =>
    (g.items || []).some((it) => it.path === location.pathname);

  const [open, setOpen] = React.useState(() => {
    const init = {};
    navGroups.forEach((g, i) => {
      if (g.title) init[i] = groupHasActive(g);
    });
    return init;
  });

  const toggle = (i) => setOpen((s) => ({ ...s, [i]: !s[i] }));

  const linkSx = (nested) => ({
    borderRadius: 2.25,
    mb: 0.25,
    pl: nested ? 4.2 : 1.5,
    color: "text.secondary",
    border: "1px solid transparent",
    "& .MuiListItemIcon-root": { color: "text.secondary", minWidth: 34 },
    "&.active": {
      color: tokens.textStrong,
      background: tokens.navActiveBg,
      border: `1px solid ${tokens.navActiveBorder}`,
      "& .MuiListItemIcon-root": { color: tokens.primary },
    },
    "&:hover": { background: tokens.raised },
  });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Brand */}
      <Box sx={{ px: 2.5, py: 2.25, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          variant="rounded"
          sx={{
            background: tokens.brandGrad,
            width: 32,
            height: 32,
            borderRadius: "8px",
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 700,
            fontSize: 18,
            color: tokens.onPrimary,
            boxShadow: tokens.shadowPrimary,
          }}
        >
          S
        </Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1, fontFamily: '"Space Grotesk", sans-serif' }}
          >
            SpinSaga
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 10 }}
          >
            Command Center
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: tokens.border }} />

      {/* Nav */}
      <List sx={{ px: 1.25, py: 1.5, flexGrow: 1, overflowY: "auto" }}>
        {navGroups.map((group, gi) => {
          // Ungrouped top-level items (e.g. Dashboard)
          if (!group.title) {
            return group.items.map((item) => {
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  end={item.path === "/"}
                  onClick={onNavigate}
                  sx={linkSx(false)}
                >
                  <ListItemIcon>{Icon && <Icon fontSize="small" />}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  />
                </ListItemButton>
              );
            });
          }

          const GroupIcon = group.icon;
          const isOpen = !!open[gi];
          const active = groupHasActive(group);
          return (
            <Box key={group.title} sx={{ mb: 0.25 }}>
              <ListItemButton onClick={() => toggle(gi)} sx={linkSx(false)}>
                <ListItemIcon>
                  {GroupIcon && (
                    <GroupIcon
                      fontSize="small"
                      sx={{ color: active ? tokens.secondary : undefined }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={group.title}
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: active ? tokens.textStrong : tokens.textDim,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                />
                {isOpen ? (
                  <ExpandMoreRoundedIcon fontSize="small" sx={{ color: tokens.textDim }} />
                ) : (
                  <ChevronRightRoundedIcon fontSize="small" sx={{ color: tokens.textDim }} />
                )}
              </ListItemButton>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {group.items.map((item) => (
                  <ListItemButton
                    key={item.path}
                    component={NavLink}
                    to={item.path}
                    onClick={onNavigate}
                    sx={linkSx(true)}
                  >
                    <ListItemIcon>
                      <FiberManualRecordIcon sx={{ fontSize: 7 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Divider sx={{ borderColor: tokens.border }} />
      <Box sx={{ p: 1.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: tokens.secondary,
              boxShadow: `0 0 10px ${tokens.secondary}`,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            All systems operational
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function Sidebar({ mobileOpen, onClose }) {
  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            background: `linear-gradient(180deg, ${tokens.bg2}, ${tokens.bg})`,
            borderRight: `1px solid ${tokens.border}`,
          },
        }}
        open
      >
        <SidebarContent />
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            bgcolor: tokens.surface,
          },
        }}
      >
        <SidebarContent onNavigate={onClose} />
      </Drawer>
    </>
  );
}
